import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { useLanguage } from "../../i18n/LanguageContext";
import { track } from "../../lib/analytics";
import { Button } from "../ui/button";
import { periodRangeLabel } from "./ScopePicker";

export function shareScopeLine(share, t, language = "en") {
  const scope = share?.scope || {};
  const count = scope.record_count ?? share?.record_count ?? 0;
  const parts = [
    t("history.shareScopeRecords").replace("{count}", String(count)),
    periodRangeLabel(scope, t, language),
  ];
  const profile = scope.include_profile ?? share?.include_summary;
  const ai = scope.include_ai_opinions ?? share?.include_ai_opinions;
  if (profile) parts.push(t("history.shareScopeProfile"));
  if (scope.include_identity) parts.push(t("history.shareScopeIdentity"));
  if (ai) parts.push(t("history.shareScopeAiShort"));
  if (scope.attach_documents) parts.push(t("history.shareScopeDocuments"));
  return parts.filter(Boolean).join(" · ");
}

export default function ManageShares({ subjectId }) {
  const { t, language } = useLanguage();
  const [shares, setShares] = useState([]);
  const [openLog, setOpenLog] = useState(null);
  const [log, setLog] = useState([]);

  const load = useCallback(async () => {
    setOpenLog(null);
    setLog([]);
    if (!subjectId) {
      setShares([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/subjects/${subjectId}/shares`);
      const items = response.data?.data?.items;
      setShares(Array.isArray(items) ? items.filter((item) => item && item.share_id) : []);
    } catch {
      setShares([]);
    }
  }, [subjectId]);

  useEffect(() => {
    load();
  }, [load]);

  const stop = async (shareId) => {
    try {
      await axios.post(`${API}/subjects/${subjectId}/shares/${shareId}/revoke`);
      track("history_share_revoked", {});
      toast.success(t("history.shareStopped"));
      if (openLog === shareId) {
        setOpenLog(null);
        setLog([]);
      }
      load();
    } catch {
      toast.error(t("history.shareError"));
    }
  };

  const toggleLog = async (shareId) => {
    if (openLog === shareId) {
      setOpenLog(null);
      setLog([]);
      return;
    }
    setOpenLog(shareId);
    try {
      const response = await axios.get(
        `${API}/subjects/${subjectId}/shares/${shareId}/access`,
      );
      const items = response.data?.data?.items;
      setLog(Array.isArray(items) ? items : []);
    } catch {
      setLog([]);
    }
  };

  return (
    <section className="space-y-2" data-testid="manage-shares">
      <h2 className="text-sm font-medium">{t("history.sharesTitle")}</h2>
      {shares.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("history.noActiveShares")}</p>
      ) : (
        <ul className="divide-y divide-border border border-border rounded-md">
          {shares.map((share) => (
            <li key={share.share_id} className="p-3 space-y-2 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p>{shareScopeLine(share, t, language)}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("history.shareCreatedAt").replace("{date}", (share.created_at || "").slice(0, 16))}
                    {" · "}
                    {t("history.shareExpiresAt").replace("{date}", (share.expires_at || "").slice(0, 16))}
                    {" · "}
                    {t("history.shareOpens").replace("{count}", String(share.open_count || 0))}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => stop(share.share_id)}
                  data-testid={`share-stop-${share.share_id}`}
                >
                  {t("history.shareStop")}
                </Button>
              </div>
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={() => toggleLog(share.share_id)}
                data-testid={`share-log-${share.share_id}`}
              >
                {t("history.shareLog")}
              </button>
              {openLog === share.share_id && (
                log.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t("history.shareLogEmpty")}</p>
                ) : (
                  <ul className="text-xs space-y-1" data-testid="share-access-log">
                    {log.map((row, index) => (
                      <li key={`${row.at}-${index}`}>
                        {(row.at || "").slice(0, 16)}
                        {" · "}
                        {t(`history.shareEvent.${row.event}`)}
                        {" · "}
                        {t(`history.shareClient.${row.client_family || "other"}`)}
                      </li>
                    ))}
                  </ul>
                )
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
